import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import {
  deleteCreditsAutoRechargeConfig,
  editCreditsAutoRechargeConfig,
  getCreditsAutoRechargeConfig,
  getWithdrawbleCredits,
  postWithdrawCredits,
  setupCreditsAutoRechargeConfig,
} from '@/common/integrations/menhir/employee/credits/credits.service';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { WithAxiosConfig } from '@/common/types/general';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

export function useGetCreditsAutoRechargeConfig(
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof getCreditsAutoRechargeConfig>>,
    any
  >
) {
  const { data: autoRechargeConfig, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.CONFIG.AUTO_RECHARGE],
    queryFn: getCreditsAutoRechargeConfig,
    ...options,
  });

  return { autoRechargeConfig, ...rest };
}

export function useSetupCreditsAutoRechargeConfig(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof setupCreditsAutoRechargeConfig>>,
    any,
    Parameters<typeof setupCreditsAutoRechargeConfig>[0]
  >
) {
  return useMutation({
    mutationFn: setupCreditsAutoRechargeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.CONFIG.AUTO_RECHARGE,
      ]);
    },
    ...options,
  });
}

export function useEditCreditsAutoRechargeConfig(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof editCreditsAutoRechargeConfig>>,
    any,
    Parameters<typeof editCreditsAutoRechargeConfig>[0]
  >
) {
  return useMutation({
    mutationFn: editCreditsAutoRechargeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.CONFIG.AUTO_RECHARGE,
      ]);
    },
    ...options,
  });
}

export function useDeleteCreditsAutoRechargeConfig(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCreditsAutoRechargeConfig>>,
    any
  >
) {
  return useMutation({
    mutationFn: deleteCreditsAutoRechargeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.CONFIG.AUTO_RECHARGE,
      ]);
    },
    ...options,
  });
}

export function useGetWithdrawableCredits(
  options?: UseQueryOptions<Awaited<ReturnType<typeof getWithdrawbleCredits>>>
) {
  const { data: credits, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.ROOT],
    queryFn: getWithdrawbleCredits,
    ...options,
  });

  return { credits, ...rest };
}

export function usePostCreditsWithdraw(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof postWithdrawCredits>>,
    any,
    WithAxiosConfig<Parameters<typeof postWithdrawCredits>[0]>
  >
) {
  return useMutation({
    mutationFn: ({ axiosConfig, ...param }) =>
      postWithdrawCredits(param, axiosConfig),

    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.CREDITS.WITHDRAWAL,
      ]);
    },
    ...options,
  });
}
